import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowForwardIos,
  Close,
  ExpandMore,
  Inventory2Outlined,
  LogoutOutlined,
  MenuRounded,
  PointOfSaleOutlined,
  SearchRounded,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import "./clientMobileNavigation.css";

const getInitials = (value) =>
  String(value || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

/** The trigger belongs to the shared client layout, not an individual page. */
export function ClientMobileTopbar({
  companyName,
  pageTitle,
  onOpen,
  onTerminal,
  hasPOSAccess = true,
  triggerRef,
}) {
  return (
    <header className="client-mob-topbar">
      <button
        ref={triggerRef}
        type="button"
        className="client-mob-menu-trigger"
        aria-label="Open workspace navigation"
        aria-haspopup="dialog"
        aria-controls="client-mobile-navigation"
        onClick={onOpen}
      >
        <MenuRounded />
      </button>
      <div className="client-mob-topbar-title">
        <span className="client-mob-topbar-eyebrow" title={companyName}>
          {companyName}
        </span>
        <strong title={pageTitle}>{pageTitle}</strong>
      </div>
      {hasPOSAccess && <button
        type="button"
        className="client-mob-topbar-terminal"
        aria-label="Open transaction terminal"
        onClick={onTerminal}
      >
        <PointOfSaleOutlined />
        <span>POS</span>
      </button>}
    </header>
  );
}

/** Phone-only drawer reuses the very same menuItems and callbacks as desktop. */
export default function ClientMobileNavigation({
  menuItems,
  companyData,
  staffData,
  accessType,
  active,
  activeDisplay,
  onClose,
  onNavigate,
  onSubNavigate,
  onTerminal,
  hasPOSAccess = true,
  onLogout,
  triggerRef,
}) {
  const drawerRef = useRef(null);
  const closeRef = useRef(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() =>
    menuItems.findIndex(
      (item) =>
        item.active === active ||
        item.children?.some((child) => child.hook === activeDisplay),
    ),
  );
  const businessName =
    companyData?.businessProfile?.basicInfo?.businessName || "Universe Inventory";
  const founder = companyData?.founder || {};
  const staffName = [
    staffData?.personalInfo?.firstName,
    staffData?.personalInfo?.lastName,
  ]
    .filter(Boolean)
    .join(" ");
  const ownerName = [founder.firstName, founder.lastName].filter(Boolean).join(" ");
  const displayName =
    (accessType === "USER_ACCESS" ? staffName : ownerName) ||
    ownerName ||
    "Workspace manager";
  const role =
    accessType === "USER_ACCESS"
      ? staffData?.role?.name || "Team member"
      : "Business owner";
  const profileImage =
    accessType === "USER_ACCESS"
      ? staffData?.files?.profilImage || staffData?.photo
      : founder?.files?.profilImage || founder?.photo;

  const filteredMenu = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return menuItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) =>
        !term ||
        item.title.toLocaleLowerCase().includes(term) ||
        item.children?.some((child) => child.title.toLocaleLowerCase().includes(term)),
      );
  }, [menuItems, query]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const elements = Array.from(
        drawerRef.current.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), a[href]',
        ),
      ).filter((element) => element.getClientRects().length);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      triggerRef?.current?.focus();
    };
  }, [onClose, triggerRef]);

  const navigateDirect = (item, index) => {
    onNavigate(item, index);
    onClose();
  };

  const navigateChild = (item, child, index) => {
    onSubNavigate(item, child, index);
    onClose();
  };

  const selectQuickPage = (activeKey, childKey) => {
    const index = menuItems.findIndex((item) => item.active === activeKey);
    if (index < 0) return;
    const item = menuItems[index];
    if (childKey) {
      const child = item.children?.find((entry) => entry.hook === childKey);
      if (child) navigateChild(item, child, index);
    } else {
      navigateDirect(item, index);
    }
  };

  return (
    <div className="client-mob-nav-portal">
      <button
        className="client-mob-nav-backdrop"
        type="button"
        aria-label="Close workspace navigation"
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        id="client-mobile-navigation"
        className="client-mob-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Workspace navigation"
        ref={drawerRef}
      >
        <div className="client-mob-nav-heading">
          <div className="client-mob-nav-brand">
            <span className="client-mob-nav-brand-mark"><Inventory2Outlined /></span>
            <div>
              <strong>Universe Inventory</strong>
              <small>Business workspace</small>
            </div>
          </div>
          <button
            type="button"
            ref={closeRef}
            className="client-mob-nav-close"
            aria-label="Close menu"
            onClick={onClose}
          >
            <Close />
          </button>
        </div>

        <div className="client-mob-nav-account">
          <span className="client-mob-nav-avatar">
            {profileImage ? <img src={profileImage} alt="" /> : getInitials(displayName)}
          </span>
          <div className="client-mob-nav-account-copy">
            <strong title={displayName}>{displayName}</strong>
            <span title={businessName}>{businessName}</span>
            <small>{role}</small>
          </div>
          <span className="client-mob-nav-account-dot" aria-label="Workspace" />
        </div>

        <div className="client-mob-nav-fixed">
          <label className="client-mob-nav-search">
            <SearchRounded aria-hidden="true" />
            <input
              type="search"
              placeholder="Search workspace..."
              aria-label="Search workspace navigation"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
                <Close fontSize="small" />
              </button>
            )}
          </label>
          <div className="client-mob-nav-shortcuts" aria-label="Workspace shortcuts">
            {menuItems.some(item => item.active === "dashboard") && <button type="button" onClick={() => selectQuickPage("dashboard")}>
              <SpaceDashboardOutlined /><span>Overview</span>
            </button>}
            {menuItems.some(item => item.active === "manage_products" && item.children?.some(child => child.hook === "items")) && <button type="button" onClick={() => selectQuickPage("manage_products", "items")}>
              <Inventory2Outlined /><span>Items</span>
            </button>}
            {hasPOSAccess && <button type="button" onClick={() => { onTerminal(); onClose(); }}>
              <PointOfSaleOutlined /><span>Terminal</span>
            </button>}
          </div>
        </div>

        <nav className="client-mob-nav-scroll" aria-label="Client account pages">
          <div className="client-mob-nav-sectionlabel">
            <span>Navigate your workspace</span>
            <small>{filteredMenu.length} sections</small>
          </div>
          {filteredMenu.length === 0 && (
            <p className="client-mob-nav-empty">No matching pages. Try a different search.</p>
          )}
          {filteredMenu.map(({ item, index }) => {
            const hasChildren = Boolean(item.children?.length);
            const isCurrent =
              active === item.active ||
              item.children?.some((child) => child.hook === activeDisplay);
            const isExpanded = Boolean(query.trim()) || expanded === index;
            const children = !query.trim() || item.title.toLowerCase().includes(query.trim().toLowerCase())
              ? item.children
              : item.children?.filter((child) => child.title.toLowerCase().includes(query.trim().toLowerCase()));
            const submenuId = `client-mob-submenu-${index}`;
            return (
              <div className="client-mob-nav-group" key={item.active || index}>
                <button
                  type="button"
                  className={`client-mob-nav-link ${isCurrent ? "is-current" : ""}`}
                  aria-current={!hasChildren && isCurrent ? "page" : undefined}
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  aria-controls={hasChildren ? submenuId : undefined}
                  onClick={() =>
                    hasChildren
                      ? setExpanded((value) => (value === index ? -1 : index))
                      : navigateDirect(item, index)
                  }
                >
                  <span className="client-mob-nav-link-icon">{item.icon}</span>
                  <span className="client-mob-nav-link-label">{item.title}</span>
                  {hasChildren ? (
                    <span className={`client-mob-nav-chevron ${isExpanded ? "expanded" : ""}`}>
                      <ExpandMore fontSize="small" />
                    </span>
                  ) : (
                    <ArrowForwardIos className="client-mob-nav-arrow" />
                  )}
                </button>
                {hasChildren && isExpanded && (
                  <div className="client-mob-nav-submenu" id={submenuId}>
                    {children?.map((child) => {
                      const selected =
                        child.hook === activeDisplay ||
                        (child.hook === "items" &&
                          ["item-explore-display", "add-new-item", "edit-item-display"].includes(activeDisplay));
                      return (
                        <button
                          type="button"
                          key={child.hook}
                          className={`client-mob-nav-sublink ${selected ? "is-current" : ""}`}
                          aria-current={selected ? "page" : undefined}
                          onClick={() => navigateChild(item, child, index)}
                        >
                          <span className="client-mob-nav-subdot" />
                          <span>{child.title}</span>
                          {selected && <span className="client-mob-nav-active-label">Current</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <footer className="client-mob-nav-footer">
          <div className="client-mob-nav-footer-caption">
            <span className="client-mob-nav-footer-indicator" />
            Your inventory workspace
          </div>
          <button type="button" className="client-mob-nav-logout" onClick={() => { onLogout(); onClose(); }}>
            <LogoutOutlined /> <span>Log out</span>
            <ArrowForwardIos />
          </button>
        </footer>
      </aside>
    </div>
  );
}
